import { useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Facebook } from "lucide-react";
import { API } from "@/lib/api";

export const W = 1080;

const loadImg = (src, cors) => new Promise((ok, bad) => { const i = new Image(); if (cors) i.crossOrigin = "anonymous"; i.onload = () => ok(i); i.onerror = bad; i.src = src; });

export const loadSilks = (urls) => Promise.all(urls.map((u) => (u ? loadImg(`${API}/silk?u=${encodeURIComponent(u)}`, true).catch(() => null) : null)));

export const drawSilk = (ctx, img, x, y, size) => {
  ctx.fillStyle = "rgba(255,255,255,0.06)"; ctx.beginPath(); ctx.roundRect(x, y, size, size, 10); ctx.fill();
  if (img) { ctx.drawImage(img, x + 4, y + 4, size - 8, size - 8); return; }
  const s = (size - 16) / 24;
  ctx.save(); ctx.translate(x + 8, y + 8); ctx.scale(s, s);
  ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 1.5; ctx.lineJoin = "round";
  ctx.stroke(new Path2D("M8 3 4 5.5 2 10l3 1.5V21h14v-9.5l3-1.5-2-4.5L16 3c-.6 1.5-2.2 2.5-4 2.5S8.6 4.5 8 3Z"));
  ctx.restore();
};

export const wrap = (ctx, text, x, y, maxW, lh, maxLines) => {
  const words = String(text || "").split(" ");
  let line = "", n = 0;
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) {
      if (++n >= maxLines) { ctx.fillText(`${line}…`, x, y); return y + lh; }
      ctx.fillText(line, x, y); y += lh; line = w;
    } else line = t;
  }
  if (line) { ctx.fillText(line, x, y); y += lh; }
  return y;
};

export const fit = (ctx, text, maxW) => {
  let t = String(text || "");
  if (ctx.measureText(t).width <= maxW) return t;
  while (t && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
};

export async function startCanvas(H) {
  await document.fonts.ready;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#0E1630"); g.addColorStop(1, "#0B101D");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  const bar = ctx.createLinearGradient(0, 0, W, 0);
  bar.addColorStop(0, "#2563eb"); bar.addColorStop(0.55, "#6366f1"); bar.addColorStop(1, "#f59e0b");
  ctx.fillStyle = bar; ctx.fillRect(0, 0, W, 10);
  try { const logo = await loadImg("/logo-horse.png"); ctx.drawImage(logo, 72, 64, 200, 200 * (logo.height / logo.width)); } catch { /* logo optional */ }
  ctx.fillStyle = "#ffffff"; ctx.font = "italic 900 54px Outfit, sans-serif"; ctx.fillText("THE WEEKEND", 300, 130);
  ctx.fillStyle = "#1E90FF"; ctx.fillText("EDGE", 300, 190);
  return { c, ctx };
}

export const finishCanvas = (c, ctx, line = "Opinion only, never a guarantee. 18+ · Gamble responsibly · 1800 858 858") => {
  ctx.fillStyle = "#64748b"; ctx.font = "500 24px 'DM Sans', sans-serif"; ctx.textAlign = "left";
  ctx.fillText(line, 72, c.height - 60);
  return new Promise((ok) => c.toBlob(ok, "image/png"));
};

export const ShareButtons = ({ make, fileName, title, text, pageUrl, testid = "share-tip-card", prefix = "" }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(await make()); } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't create the image"); } setBusy(false); };
  const download = (blob) => {
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href: url, download: fileName }).click();
    URL.revokeObjectURL(url); toast.success("Image saved — post it to Instagram or Facebook");
  };
  const share = (blob) => {
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) return navigator.share({ files: [file], title, text });
    download(blob);
  };
  const fb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, "_blank", "noopener,noreferrer,width=640,height=560");

  return (
    <div className="flex flex-wrap gap-2 pt-1" data-testid={testid}>
      <button data-testid={`${prefix}share-card-button`} disabled={busy} onClick={() => run(share)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-xs font-semibold disabled:opacity-50">
        <Share2 className="w-3.5 h-3.5" />{busy ? "Creating…" : "Share image"}
      </button>
      <button data-testid={`${prefix}download-card-button`} disabled={busy} onClick={() => run(download)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs">
        <Download className="w-3.5 h-3.5" />Download
      </button>
      <button data-testid={`${prefix}facebook-share-button`} onClick={fb} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs">
        <Facebook className="w-3.5 h-3.5" />Facebook link
      </button>
    </div>
  );
};
